import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Utils } from 'formiojs';
import {
	Observable,
	Subscription,
	catchError,
	debounceTime,
	distinctUntilChanged,
	iif,
	map,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import {
	FormioCustomComponent,
	ResourceNameService,
} from 'dp-formio';
import { EvaluationDisplayPipe } from './pipe/evaluation-display.pipe';
declare var $: any;

export enum HooksEnum {
	AfterLoadOptions = 'afterLoadOptions',
	OnValueChange = 'onValueChange',
	OnInit = 'onInt',
}

@Component({
	selector: 'app-resource-autocomplete-wrapper',
	templateUrl: './resource-autocomplete-wrapper.component.html',
	// styleUrls: ['./resource-autocomplete-wrapper.component.scss'],
	encapsulation: ViewEncapsulation.None,
	// providers: [FormioLoader]
	providers: [EvaluationDisplayPipe],
})
export class ResourceAutocompleteWrapperComponent
	implements FormioCustomComponent<any>, OnChanges, OnInit, OnDestroy
{
	@ViewChild(MatAutocompleteTrigger)
	autocompleteTrigger: MatAutocompleteTrigger;

	dir: 'rtl' | 'ltr';
	@Input() required: boolean;
	@Input() value: any = {};
	@Output() valueChange = new EventEmitter<any>();
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;
	@Input() data: any;
	@Input() customOptions: any;
	@Input() formioComponent: any;

	labelHint: any;

	private subs: Subscription[] = [];
	set sub(s: Subscription) {
		this.subs.push(s);
	}

	filterControl = new FormControl();
	filteredOptions: Observable<any>;
	optionsList = [];

	constructor(
		private router: Router,
		private resourceNameService: ResourceNameService,
		private translateService: TranslateService,
		private evaluationDisplayPipe: EvaluationDisplayPipe
	) {
		this._prepareDir();
	}

	displayAs = (item: any) => {
		if (!item || Object.keys(item).length === 0) return '';
		return this.evaluationDisplayPipe.transform(
			item,
			this.customOptions?.displayField
		);
	};

	ngOnChanges(changes: SimpleChanges) {
		this.filterControl.setValue(this.value);
		this._disableOrEnableCtrl();
		if (this.customOptions) {
			this._updateFilteredList();
		}
		this.initLabelHint();
	}

	ngOnInit(): void {
		this._runHookIfActive(HooksEnum.OnInit, {
			optionsList: this.optionsList,
			selectedItem: this.value,
		});
	}

	ngOnDestroy() {
		this.subs.forEach((s) => s.unsubscribe());
		this.subs = [];
	}

	notifyValue() {
		if (this.customOptions?.selectedValue) {
			this.value = Utils.interpolate(this.customOptions.selectedValue, {
				data: this.data,
				selectedItem: this.filterControl.value,
				translate: this.translateService,
			});
			this.valueChange.emit(this.value);
		} else {
			this.resetValue();
		}
		this._runHookIfActive(HooksEnum.OnValueChange, {
			optionsList: this.optionsList,
			selectedItem: this.value,
			item: this.filterControl.value,
		});
		this.initLabelHint();
	}

	openAutocomplete(): void {
		this.autocompleteTrigger.openPanel();
	}

	private _disableOrEnableCtrl() {
		if (this.disabled || this.viewOnly) {
			this.filterControl.disable();
		} else {
			this.filterControl.enable();
		}
	}

	private _updateFilteredList() {
		let evaluatedUrl = '';
		if (this.customOptions.url)
			evaluatedUrl = Utils.interpolate(this.customOptions.url, {
				config: this.formioComponent.root.form.config,
				data: this.data,
				translate: this.translateService.instant,
			});

		this.filteredOptions = this.filterControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged(),
			map((filterValue) => {
				if (this.customOptions?.enableUserEntry) this.notifyValue();
				const query = this.customOptions?.query
					? Utils.evaluate(this.customOptions.query, {
							data: this.data,
							filterValue,
							translate: this.translateService,
					  })
					: '';
				return query;
			}),
			switchMap((query) =>
				iif(
					() => !!evaluatedUrl,
					this.resourceNameService.load(evaluatedUrl, query),
					this.resourceNameService.loadCustomByKeyExists(
						this.customOptions.resource,
						query
					)
				).pipe(
					catchError((error) => {
						console.log(error);
						return [];
					})
				)
			),
			tap((data) => {
				this.optionsList = data;
				this._runHookIfActive(HooksEnum.AfterLoadOptions, {
					optionsList: this.optionsList,
				});
			})
		);
	}

	private resetValue() {
		this.value = null;
		this.valueChange.emit(this.value);
	}

	private _runHookIfActive(_hook: string, params: Record<string, any> = {}) {
		const hook = this._getHook(_hook);
		return hook?.active ? this._evaluate(hook?.execute, params) : null;
	}

	private _getHook(hook: string) {
		const _hook = this.customOptions?.hooks?.[hook];
		return _hook ?? { active: false, execute: '' };
	}

	private _evaluate(query: string, params: Record<string, any> = {}) {
		return Utils.evaluate(query, {
			utils: Utils,
			instance: this.formioComponent,
			submission: this.formioComponent?.root?.data,
			translate: this.translateService,
			data: this.data,
			value: this.value,
			component: this.formioComponent.component,
			...params,
		});
	}

	private _prepareDir() {
		this.sub = (
			this.translateService.onLangChange as Observable<LangChangeEvent>
		)
			.pipe(
				startWith({
					lang: this.translateService.currentLang,
				}),
				map((data) => (data.lang === 'ar' ? 'rtl' : 'ltr'))
			)
			.subscribe((dir) => {
				this.dir = dir;
			});
	}

	private initLabelHint() {
		if (this.customOptions?.labelHint) {
			this.labelHint = Utils.evaluate(this.customOptions?.labelHint, {
				data: this.data,
			});
		}
	}
}
